import * as React from 'react';
import { DesktopSearchBlockInput } from 'shared/components/DesktopSearchBlockInput/DesktopSearchBlockInput';
import { useApplicationContext } from '../ApplicationContext';
import { ISuggestion } from 'shared/types/suggestions';
import { IFullTipSchema } from 'shared/repositories/text-search/entities/tips/FullTipSchema';
import { DropdownPopover } from '@cian/ui-kit';
import { debounce } from 'throttle-debounce';
import { fetchSuggestionsByText } from 'shared/services/fetchSuggestionsByText';
import { fetchSuggestion } from 'shared/services/fetchSuggestion';
import { ETelemetryEvents } from 'shared/types/telemetry';
import { trackFirstSearchInput, trackInputInFocused, trackSearchSubmit, trackTipClick } from './tracking';
import { ESearchType } from 'shared/types/searchType';
import { selectQueryString } from 'shared/selectors/queryString';
import { useDispatch, useSelector } from 'react-redux';
import { setQueryString } from 'shared/actions/queryString';
import { selectFilterJsonQuery } from 'shared/selectors';
import { dispatchSearchUpdatedEvent } from 'shared/utils/searchUpdatedEvent';
import { EPageType } from 'shared/types/pageType';
import { useTipsContext } from '../SearchTipsContainer/provider';
import { SearchBlockContent } from 'shared/components/SearchBlockContent';
import { SearchTipsContainer } from '../SearchTipsContainer/SearchTipsContainer';
import { SearchBlockSuggestsContainer } from '../SearchBlockSuggestsContainer';

const SEARCH_CLICK_EVENT = 'search:click';

export const DesktopSearchInputContainer: React.FC = () => {
  const { clickedTip, loadSearchTips } = useTipsContext();

  const context = useApplicationContext();
  const { userLocationId, pageType } = context.custom;

  const jsonQuery = useSelector(selectFilterJsonQuery);
  const query = useSelector(selectQueryString);
  const dispatch = useDispatch();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isNotFound, setIsNotFound] = React.useState<boolean>(false);
  const [suggestions, setSuggestions] = React.useState<ISuggestion[]>([]);

  const historySuggests =
    typeof window !== 'undefined' && window.localStorage ? JSON.parse(localStorage.getItem('searchQuery') || '{}') : {};
  const isSearchPage = pageType === EPageType.SEARCH;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const hasTrackedFirstInput = React.useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = React.useCallback(
    debounce(500, async (query: string) => {
      setLoading(true);

      await loadSearchTips(query);

      const suggestions = await fetchSuggestionsByText(context, query, jsonQuery, userLocationId.toString());
      setSuggestions(
        historySuggests && historySuggests.url && query.trim() === '' ? [historySuggests, ...suggestions] : suggestions,
      );
      setIsNotFound(!suggestions.length);

      setLoading(false);

      if (!hasTrackedFirstInput.current && query.trim() !== '') {
        trackFirstSearchInput();
        hasTrackedFirstInput.current = true;
      }
    }),
    [jsonQuery, query, userLocationId],
  );

  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsFocused(false);
        dispatch(setQueryString(''));
        handleSearch('');
        setIsNotFound(false);
        setLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [dispatch]);

  const handleQueryChange = React.useCallback(
    (value: string) => {
      dispatch(setQueryString(value));
      setIsNotFound(false);

      handleSearch(value);
    },
    [handleSearch],
  );

  const handleSubmit = React.useCallback(
    async (isMap: boolean) => {
      setLoading(true);
      let url = await fetchSuggestion(context, { text: query, jsonQuery, geo: userLocationId.toString(), isMap });

      if (url) {
        context.telemetry.increment(ETelemetryEvents.SearchSuggestionLinkSuccess);
      }

      if (!url) {
        if (!suggestions.length) {
          context.telemetry.increment(ETelemetryEvents.FirstSuggestionNotFound);

          return;
        }

        url = suggestions.find(item => item.isDefault)?.url || suggestions[0].url;

        context.telemetry.increment(
          url ? ETelemetryEvents.FirstSuggestionLinkSuccess : ETelemetryEvents.FirstSuggestionLinkFail,
        );

        if (!url) {
          return;
        }
      }

      if (query && query.trim() !== '') {
        localStorage.setItem(
          'searchQuery',
          JSON.stringify({
            query,
            url,
            type: 'last_search',
            isUserSuggest: true,
          }),
        );
      }

      const parsedUrl = new URL(url, window.location.origin);
      parsedUrl.searchParams.set('source', 'search_string');

      url = parsedUrl.toString();

      const isDuplicateSuggest = suggestions.length === 1 && suggestions[0].isUserSuggest;

      trackSearchSubmit({
        query,
        totalSuggest: `${suggestions.length}`,
        url,
        isDuplicateSuggest,
      });

      dispatchSearchUpdatedEvent({ url, type: isMap ? ESearchType.Map : ESearchType.List });

      setLoading(false);
    },
    [context, query, suggestions, userLocationId, jsonQuery],
  );

  const handleTips = React.useCallback(
    async (tips?: IFullTipSchema) => {
      if (tips) {
        trackTipClick({ clickedTip: tips });

        handleQueryChange(tips.fullText);
      } else {
        await loadSearchTips(query);
      }
    },
    [query],
  );

  const handleSubmitRef = React.useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  React.useEffect(() => {
    const handleSearchClick = (event: CustomEvent) => {
      const eventType = event.detail?.type;
      const isMapSearch = eventType === ESearchType.Map || eventType === 'map';

      setTimeout(() => {
        handleSubmitRef.current(isMapSearch);
      }, 0);
    };

    document.addEventListener(SEARCH_CLICK_EVENT, handleSearchClick as EventListener);

    return () => {
      document.removeEventListener(SEARCH_CLICK_EVENT, handleSearchClick as EventListener);
    };
  }, []);

  React.useEffect(() => {
    handleQueryChange('');
    loadSearchTips('');
  }, []);

  React.useEffect(() => {
    if (clickedTip) {
      handleQueryChange(clickedTip.text);
      handleTips(clickedTip);
      setIsFocused(true);
      trackInputInFocused();
      inputRef.current?.getElementsByTagName('input')[0]?.focus();
    }
  }, [clickedTip, handleQueryChange, handleTips]);

  return (
    <DropdownPopover
      open={isFocused}
      sameWidth={'width'}
      zIndex={8}
      content={
        <div
          onMouseDown={e => {
            // Предотвращаем потерю фокуса при клике внутри поповера
            e.preventDefault();
          }}
        >
          <SearchBlockContent
            searchInput={null}
            tips={<SearchTipsContainer onClick={handleTips} desktopStyle={true} />}
            suggests={
              <SearchBlockSuggestsContainer
                query={query}
                suggestions={suggestions}
                withNotFoundText={isNotFound}
                setLoading={setLoading}
              />
            }
            isDesktop={true}
          />
        </div>
      }
    >
      <DesktopSearchBlockInput
        isSearchPage={isSearchPage}
        query={query}
        loading={loading}
        inputRef={inputRef}
        onChangeFocus={setIsFocused}
        onQueryChange={handleQueryChange}
        onSubmit={handleSubmit}
      />
    </DropdownPopover>
  );
};
